import { useCallback, useEffect, useRef, useState } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { readRecentSearches, writeRecentSearches } from "@shared/recentSearches";

const VERIFIABLE_TYPES = ["person", "listing", "project"];

// Recent searches are stored blobs from Elasticsearch, so older entries may lack either field.
const searchKey = ({ objectType, objectId } = {}) => {
  if (!objectType || !objectId) return null;

  const type = String(objectType).toLowerCase();
  return VERIFIABLE_TYPES.includes(type) ? `${type}:${objectId}` : null;
};

/**
 * Prunes Recent Searches for records that no longer exist or are no longer accessible.
 * Pruning is destructive, so every failure path leaves the list untouched.
 */
const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] = useState(readRecentSearches);
  const isValidating = useRef(false);
  const controller = useRef(null);

  useEffect(() => () => controller.current?.abort(), []);

  const validateRecentSearches = useCallback(() => {
    if (isValidating.current) return;

    const searches = readRecentSearches();
    const keys = searches.map(searchKey).filter(Boolean);
    if (keys.length === 0) return;

    controller.current = new AbortController();

    let request;
    try {
      isValidating.current = true;
      request = caseTransformingAxios.get("/accounts/verify_recent_searches.json", {
        params: { keys },
        signal: controller.current.signal,
      });
    } catch {
      // A synchronous throw (e.g. axios misconfiguration) must not wedge the latch forever.
      isValidating.current = false;
      return;
    }

    request
      .then(({ data }) => {
        const missing = data?.missing;
        if (!Array.isArray(missing) || missing.length === 0) return;

        const missingKeys = new Set(missing);
        const surviving = searches.filter((search) => !missingKeys.has(searchKey(search)));
        if (surviving.length === searches.length) return;

        writeRecentSearches(surviving);
        setRecentSearches(surviving);
      })
      .catch(() => {
        // Network error, abort, or a 500. Leave the list alone rather than prune on bad information.
      })
      .finally(() => {
        isValidating.current = false;
      });
  }, []);

  return { recentSearches, validateRecentSearches };
};

export default useRecentSearches;
